import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/** Extra downward scroll (px) in the trigger zone before auto-load yields to the footer. */
const NEXT_PAGE_PUSH_DELTA_PX = 320;

/** Downward scroll speed (px/ms) treated as a flick past the product grid. */
const NEXT_PAGE_VELOCITY_PX_PER_MS = 1.75;

/** Idle time after which a push-past gesture resets. */
const PUSH_GESTURE_IDLE_MS = 400;

/** Delay before auto-loading so a hard scroll can cancel it. */
const NEXT_PAGE_LOAD_DELAY_MS = 150;

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous sentinel.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next sentinel.
 * @property {HTMLButtonElement} [loadMoreNext] - Load more button shown after pushing past infinite scroll.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #manualNextLoad = false;

  /** @type {boolean} */
  #loadingNext = false;

  /** @type {boolean} */
  #renderingNext = false;

  /** @type {number} */
  #pushDeltaY = 0;

  /** @type {number} */
  #pushIdleTimer = 0;

  /** @type {number} */
  #nextPageLoadTimer = 0;

  /** @type {number} */
  #lastScrollY = 0;

  /** @type {number} */
  #lastScrollTs = 0;

  /** @type {number} */
  #velocityY = 0;

  /** @type {number} */
  #lastTouchY = 0;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#lastScrollY = window.scrollY;
    this.#lastScrollTs = performance.now();

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.#bindPushPastListeners();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.#clearNextPageLoadTimer();
    this.#unbindPushPastListeners();
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#scheduleNextPageLoad();
              }
            } else if (entry.target === this.refs.viewMoreNext) {
              this.#clearNextPageLoadTimer();
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext && !this.#manualNextLoad) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Loads the next page when the user clicks Load more after pushing past infinite scroll.
   * @param {Event} [event]
   */
  async handleLoadMoreNext(event) {
    event?.preventDefault();

    if (this.#loadingNext) return;

    const button = this.refs.loadMoreNext;
    this.#loadingNext = true;

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    try {
      await this.#renderNextPage({ preserveScroll: true });
    } finally {
      this.#loadingNext = false;

      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }

      this.#syncLoadMoreButton();
    }
  }

  #bindPushPastListeners() {
    if (!this.refs.viewMoreNext) return;

    window.addEventListener('wheel', this.#onWheel, { passive: true });
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    window.addEventListener('touchmove', this.#onTouchMove, { passive: true });
    window.addEventListener('keydown', this.#onKeyDown);
  }

  #unbindPushPastListeners() {
    window.removeEventListener('wheel', this.#onWheel);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('touchstart', this.#onTouchStart);
    window.removeEventListener('touchmove', this.#onTouchMove);
    window.removeEventListener('keydown', this.#onKeyDown);
    window.clearTimeout(this.#pushIdleTimer);
  }

  /**
   * @param {WheelEvent} event
   */
  #onWheel = (event) => {
    const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
    if (delta > 0) this.#registerPushIntent(delta);
  };

  #onScroll = () => {
    const now = performance.now();
    const y = window.scrollY;
    const dt = now - this.#lastScrollTs;

    if (dt > 0 && dt < 120) {
      this.#velocityY = (y - this.#lastScrollY) / dt;
    } else if (dt >= 120) {
      this.#velocityY = 0;
    }

    this.#lastScrollY = y;
    this.#lastScrollTs = now;

    if (this.#manualNextLoad && this.#shouldResumeAutoLoad()) {
      this.#exitManualNextLoad();
    }
  };

  /**
   * @param {TouchEvent} event
   */
  #onTouchStart = (event) => {
    this.#lastTouchY = event.touches[0]?.clientY ?? 0;
  };

  /**
   * @param {TouchEvent} event
   */
  #onTouchMove = (event) => {
    const y = event.touches[0]?.clientY ?? this.#lastTouchY;
    const delta = this.#lastTouchY - y;
    this.#lastTouchY = y;
    if (delta > 0) this.#registerPushIntent(delta);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (this.#isTypingTarget(event.target)) return;
    if (event.target === this.refs.loadMoreNext) return;

    if (event.key === 'End' && this.#hasNextPage()) {
      this.#enterManualNextLoad();
      return;
    }

    if (event.key === 'PageDown') {
      this.#registerPushIntent(400);
      return;
    }

    if (event.key === 'ArrowDown' || event.key === ' ') {
      this.#registerPushIntent(80);
    }
  };

  /**
   * @param {EventTarget | null} target
   * @returns {boolean}
   */
  #isTypingTarget(target) {
    if (!(target instanceof HTMLElement)) return false;

    return (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      target.isContentEditable
    );
  }

  /**
   * @param {number} deltaY
   */
  #registerPushIntent(deltaY) {
    if (this.#manualNextLoad || deltaY <= 0 || !this.#hasNextPage()) return;
    if (!this.#isInNextPageTriggerZone()) return;

    this.#pushDeltaY += deltaY;
    window.clearTimeout(this.#pushIdleTimer);
    this.#pushIdleTimer = window.setTimeout(() => {
      this.#pushDeltaY = 0;
    }, PUSH_GESTURE_IDLE_MS);

    if (this.#shouldPassThroughNextPage()) {
      this.#enterManualNextLoad();
    }
  }

  #scheduleNextPageLoad() {
    if (this.#manualNextLoad || this.#loadingNext || this.#renderingNext) return;

    if (this.#shouldPassThroughNextPage()) {
      this.#enterManualNextLoad();
      return;
    }

    this.#clearNextPageLoadTimer();
    this.#nextPageLoadTimer = window.setTimeout(() => {
      this.#nextPageLoadTimer = 0;

      if (this.#manualNextLoad || this.#loadingNext || this.#renderingNext) return;

      if (this.#shouldPassThroughNextPage()) {
        this.#enterManualNextLoad();
        return;
      }

      this.#renderNextPage();
    }, NEXT_PAGE_LOAD_DELAY_MS);
  }

  #clearNextPageLoadTimer() {
    if (this.#nextPageLoadTimer) {
      window.clearTimeout(this.#nextPageLoadTimer);
      this.#nextPageLoadTimer = 0;
    }
  }

  /**
   * @returns {boolean}
   */
  #shouldPassThroughNextPage() {
    if (this.#velocityY > NEXT_PAGE_VELOCITY_PX_PER_MS) return true;
    if (this.#pushDeltaY >= NEXT_PAGE_PUSH_DELTA_PX) return true;
    return this.#hasScrolledPastGrid();
  }

  /**
   * True when the next-page sentinel has been scrolled into the upper part of the viewport,
   * meaning the user is heading into content below the product grid.
   * @returns {boolean}
   */
  #hasScrolledPastGrid() {
    const sentinel = this.refs.viewMoreNext;
    if (!sentinel) return false;

    return sentinel.getBoundingClientRect().top < window.innerHeight * 0.4;
  }

  /**
   * @returns {boolean}
   */
  #isInNextPageTriggerZone() {
    const sentinel = this.refs.viewMoreNext;
    if (!sentinel) return false;

    const top = sentinel.getBoundingClientRect().top;
    return top < window.innerHeight + 120;
  }

  /**
   * @returns {boolean}
   */
  #shouldResumeAutoLoad() {
    const sentinel = this.refs.viewMoreNext;
    if (!sentinel) return false;

    return sentinel.getBoundingClientRect().top > window.innerHeight;
  }

  /**
   * @returns {boolean}
   */
  #hasNextPage() {
    const page = this.#getPage('next');
    return Boolean(page && this.#shouldUsePage(page));
  }

  #enterManualNextLoad() {
    if (this.#manualNextLoad) return;

    this.#manualNextLoad = true;
    this.#pushDeltaY = 0;
    this.#clearNextPageLoadTimer();

    const { viewMoreNext } = this.refs;
    if (viewMoreNext && this.infinityScrollObserver) {
      this.infinityScrollObserver.unobserve(viewMoreNext);
    }

    this.#syncLoadMoreButton();
  }

  #exitManualNextLoad() {
    if (!this.#manualNextLoad) return;

    this.#manualNextLoad = false;
    this.#pushDeltaY = 0;
    this.#syncLoadMoreButton();

    const { viewMoreNext } = this.refs;
    if (viewMoreNext && this.infinityScrollObserver && this.isConnected) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  #syncLoadMoreButton() {
    const button = this.refs.loadMoreNext;
    if (!button) return;

    const shouldShow = this.#manualNextLoad && this.#hasNextPage();

    if (!shouldShow && document.activeElement === button) {
      const cards = this.refs.cards;
      const lastCard = Array.isArray(cards) ? cards[cards.length - 1] : null;
      const loadedPage = lastCard?.dataset.page;
      const firstOfPage = Array.isArray(cards)
        ? cards.find((card) => card.dataset.page === loadedPage)
        : null;
      const focusable = firstOfPage?.querySelector('a, button');
      if (focusable instanceof HTMLElement) {
        focusable.focus({ preventScroll: true });
      }
    }

    button.hidden = !shouldShow;
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * @param {{ preserveScroll?: boolean }} [options]
   */
  async #renderNextPage(options = {}) {
    const { grid } = this.refs;

    if (!grid || this.#renderingNext) return;

    this.#renderingNext = true;

    try {
      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      const scrollY = options.preserveScroll ? window.scrollY : null;
      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper.processNewElements();

      history.pushState('', '', nextPage.url.toString());

      if (scrollY !== null) {
        window.scrollTo({ top: scrollY, behavior: 'instant' });
        requestAnimationFrame(() => {
          window.scrollTo({ top: scrollY, behavior: 'instant' });
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } finally {
      this.#renderingNext = false;
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#exitManualNextLoad();
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
